import { Check, ChevronDown, LogOut, ShieldCheck, UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";

import { frontendConfig } from "../api/config.js";
import { useAuth } from "../auth/AuthProvider.js";
import { useActiveMembership } from "./ActiveMembership.js";
import { InternalTools } from "./InternalTools.js";
import { groupPermissions, ROLE_LABELS } from "./roles.js";

/**
 * Konto, Rolle und Rechte — oben rechts, in jeder Shell dieselbe Komponente.
 *
 * Wer mehrere Rollen hat, wechselt hier; wer nur eine hat, sieht sie wenigstens. Die
 * Rechte stehen ausgeschrieben darunter, weil „darf ich das eigentlich?" sonst nur
 * durch Ausprobieren zu beantworten wäre.
 */
export function AccountMenu(): JSX.Element {
  const { signOut } = useAuth();
  const { me, memberships, active, assumed, setActive, permissions } = useActiveMembership();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Ein Klick daneben schliesst das Menü. Ohne das bliebe es offen, sobald man
  // woanders hinfasst — auf dem Telefon besonders störend.
  useEffect(() => {
    if (!open) {
      return;
    }
    const schliessen = (event: MouseEvent): void => {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const taste = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", schliessen);
    document.addEventListener("keydown", taste);
    return () => {
      document.removeEventListener("mousedown", schliessen);
      document.removeEventListener("keydown", taste);
    };
  }, [open]);

  const rolle = active ? ROLE_LABELS[active.role] : null;
  const initialen = (me?.displayName ?? "?")
    .split(" ")
    .map((teil) => teil[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        className="flex min-h-[44px] items-center gap-2 rounded-full border border-line bg-surface pl-1.5 pr-3 text-left"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span
          className="grid h-8 w-8 flex-none place-items-center rounded-full text-[12px] font-extrabold text-white"
          style={{ background: "var(--role, var(--brand))" }}
          aria-hidden="true"
        >
          {initialen}
        </span>
        <span className="hidden min-w-0 sm:block">
          <span className="block truncate text-[13px] font-bold leading-tight text-ink">
            {me?.displayName ?? "Konto"}
          </span>
          <span className="block truncate text-[11px] leading-tight text-ink-3">
            {rolle ? `${rolle.label}${assumed ? " (intern)" : ""} · ${active?.tenantName}` : "Privat"}
          </span>
        </span>
        <ChevronDown size={15} className="flex-none text-ink-3" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="absolute right-0 z-30 mt-2 w-[19rem] overflow-hidden rounded-lg border border-line bg-surface shadow-3"
          role="menu"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="truncate text-[14px] font-bold text-ink">{me?.displayName}</p>
            <p className="truncate text-[12.5px] text-ink-2">{me?.email}</p>
          </div>

          <div className="max-h-[22rem] overflow-y-auto">
            <p className="micro px-4 pb-1 pt-3">Ansicht</p>

            <RoleOption
              label="Privat"
              sublabel="Buchen wie jeder andere"
              active={active === null}
              onClick={() => {
                setActive(null);
                setOpen(false);
              }}
            />

            {memberships.map((entry) => (
              <RoleOption
                key={entry.tenantId}
                label={entry.tenantName}
                sublabel={ROLE_LABELS[entry.role].label}
                active={!assumed && active?.tenantId === entry.tenantId}
                onClick={() => {
                  setActive(entry.tenantId);
                  setOpen(false);
                }}
              />
            ))}

            {active && assumed && (
              <RoleOption
                label={active.tenantName}
                sublabel={`${ROLE_LABELS[active.role].label} · intern eingenommen`}
                active
                onClick={() => setOpen(false)}
              />
            )}

            <InternalTools onDone={() => setOpen(false)} />

            {active && (
              <>
                <p className="micro px-4 pb-1 pt-3">Meine Rechte</p>
                <p className="px-4 pb-2 text-[12.5px] leading-relaxed text-ink-2">
                  {ROLE_LABELS[active.role].description}
                </p>
                <ul className="px-4 pb-3">
                  {groupPermissions(permissions).map(([bereich, rechte]) => (
                    <li key={bereich} className="mb-2">
                      <p className="text-[11.5px] font-extrabold text-ink-2">{bereich}</p>
                      <p className="num text-[11px] leading-relaxed text-ink-3">
                        {rechte.join(" · ")}
                      </p>
                    </li>
                  ))}
                </ul>
                <p className="flex items-start gap-1.5 px-4 pb-3 text-[11.5px] leading-relaxed text-ink-3">
                  <ShieldCheck size={13} className="mt-0.5 flex-none" aria-hidden="true" />
                  Rechte vergibt das Unternehmen. Geprüft werden sie auf dem Server, nicht hier.
                </p>
              </>
            )}
          </div>

          <button
            type="button"
            className="flex min-h-[44px] w-full items-center gap-2 border-t border-line px-4 text-[13.5px] font-bold text-ink-2 hover:text-ink"
            onClick={() => void signOut()}
            role="menuitem"
          >
            <LogOut size={16} aria-hidden="true" />
            Abmelden
          </button>

          <p className="num border-t border-line px-4 py-2 text-center text-[10.5px] text-ink-3">
            {frontendConfig().version}
          </p>
        </div>
      )}
    </div>
  );
}

function RoleOption({
  label,
  sublabel,
  active,
  onClick,
}: {
  label: string;
  sublabel: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      className="flex min-h-[44px] w-full items-center gap-2.5 px-4 text-left hover:bg-surface-2"
      onClick={onClick}
    >
      <UserRound size={16} className="flex-none text-ink-3" aria-hidden="true" />
      <span className="min-w-0 flex-1 py-1.5">
        <span className="block truncate text-[13.5px] font-bold text-ink">{label}</span>
        <span className="block truncate text-[11.5px] text-ink-3">{sublabel}</span>
      </span>
      {/* Die Wahl trägt nicht nur Farbe: das Häkchen sagt dasselbe noch einmal. */}
      {active && <Check size={16} className="flex-none text-role" aria-hidden="true" />}
    </button>
  );
}
